import React, { useState, useEffect } from 'react';
import couponService from '../../../services/couponService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DISCOUNT_TYPES = [
  { value: 'percentage', label: 'Yüzde (%)' },
  { value: 'fixed', label: 'Sabit Tutar (₺)' },
];

function CreateCouponModal({ onClose, onCreated, users }) {
  const [form, setForm] = useState({
    code: '',
    discountType: 'percentage',
    discountValue: '',
    minOrderAmount: '',
    maxDiscountAmount: '',
    expiresAt: '',
    usageLimit: '',
    attributedUserId: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) code += chars?.[Math.floor(Math.random() * chars?.length)];
    setForm(prev => ({ ...prev, code }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.code?.trim()) { setError('Kupon kodu zorunludur'); return; }
    if (!form?.discountValue || Number(form?.discountValue) <= 0) { setError('İndirim değeri geçersiz'); return; }
    if (form?.discountType === 'percentage' && Number(form?.discountValue) > 100) { setError('Yüzde indirim 100\'den fazla olamaz'); return; }
    try {
      setLoading(true);
      setError('');
      await couponService?.createCoupon(form);
      onCreated();
      onClose();
    } catch (err) {
      setError(err?.message || 'Kupon oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="text-lg font-bold text-foreground">Yeni Kupon Oluştur</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-destructive text-sm">{error}</div>
          )}

          {/* Code */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Kupon Kodu *</label>
            <div className="flex gap-2">
              <Input
                name="code"
                value={form?.code}
                onChange={handleChange}
                placeholder="ÖRNEK2026"
                className="uppercase flex-1"
              />
              <Button type="button" variant="outline" size="sm" onClick={generateCode}>
                <Icon name="Shuffle" size={14} />
              </Button>
            </div>
          </div>

          {/* Discount Type */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">İndirim Türü *</label>
            <select
              name="discountType"
              value={form?.discountType}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary"
            >
              {DISCOUNT_TYPES?.map(t => <option key={t?.value} value={t?.value}>{t?.label}</option>)}
            </select>
          </div>

          {/* Discount Value */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              İndirim Değeri * {form?.discountType === 'percentage' ? '(%)' : '(₺)'}
            </label>
            <Input
              name="discountValue"
              type="number"
              min="0"
              step="0.01"
              value={form?.discountValue}
              onChange={handleChange}
              placeholder={form?.discountType === 'percentage' ? '10' : '50'}
            />
          </div>

          {/* Min Order Amount */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Minimum Sipariş Tutarı (₺)</label>
            <Input
              name="minOrderAmount"
              type="number"
              min="0"
              step="0.01"
              value={form?.minOrderAmount}
              onChange={handleChange}
              placeholder="0 (isteğe bağlı)"
            />
          </div>

          {/* Max Discount (only for percentage) */}
          {form?.discountType === 'percentage' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Maksimum İndirim Tutarı (₺)</label>
              <Input
                name="maxDiscountAmount"
                type="number"
                min="0"
                step="0.01"
                value={form?.maxDiscountAmount}
                onChange={handleChange}
                placeholder="Sınırsız (isteğe bağlı)"
              />
            </div>
          )}

          {/* Expiry Date */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Son Kullanma Tarihi</label>
            <Input
              name="expiresAt"
              type="datetime-local"
              value={form?.expiresAt}
              onChange={handleChange}
            />
          </div>

          {/* Usage Limit */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Kullanım Limiti</label>
            <Input
              name="usageLimit"
              type="number"
              min="1"
              value={form?.usageLimit}
              onChange={handleChange}
              placeholder="Sınırsız (isteğe bağlı)"
            />
          </div>

          {/* Attributed User */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Kullanıcı Bağlantısı (Personel/Influencer)</label>
            <select
              name="attributedUserId"
              value={form?.attributedUserId}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary"
            >
              <option value="">Bağlantı yok</option>
              {users?.map(u => (
                <option key={u?.id} value={u?.id}>{u?.full_name || u?.email}</option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Açıklama</label>
            <Input
              name="description"
              value={form?.description}
              onChange={handleChange}
              placeholder="İç not (isteğe bağlı)"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>İptal</Button>
            <Button type="submit" className="flex-1" disabled={loading}>
              {loading ? 'Oluşturuluyor...' : 'Kupon Oluştur'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CouponManagement() {
  const [activeView, setActiveView] = useState('list'); // 'list' | 'stats'
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadCoupons();
    loadUsers();
  }, []);

  useEffect(() => {
    if (activeView === 'stats') loadStats();
  }, [activeView]);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await couponService?.getAllCoupons();
      setCoupons(data);
    } catch (err) {
      setError(err?.message || 'Kuponlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const data = await couponService?.getCouponStats();
      setStats(data);
    } catch (err) {
      console.error('Stats load error:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await couponService?.getUsers();
      setUsers(data);
    } catch (err) {
      console.error('Users load error:', err);
    }
  };

  const handleToggleActive = async (coupon) => {
    try {
      await couponService?.updateCoupon(coupon?.id, { is_active: !coupon?.is_active });
      setCoupons(prev => prev?.map(c => c?.id === coupon?.id ? { ...c, is_active: !c?.is_active } : c));
    } catch (err) {
      alert('Güncelleme başarısız: ' + err?.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kuponu silmek istediğinizden emin misiniz?')) return;
    try {
      await couponService?.deleteCoupon(id);
      setCoupons(prev => prev?.filter(c => c?.id !== id));
    } catch (err) {
      alert('Silme başarısız: ' + err?.message);
    }
  };

  const filteredCoupons = coupons?.filter(c =>
    c?.code?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    c?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const isExpired = (coupon) => coupon?.expires_at && new Date(coupon?.expires_at) < new Date();
  const isLimitReached = (coupon) => coupon?.usage_limit !== null && coupon?.usage_count >= coupon?.usage_limit;

  const getStatusBadge = (coupon) => {
    if (!coupon?.is_active) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">Pasif</span>;
    if (isExpired(coupon)) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-destructive/10 text-destructive">Süresi Doldu</span>;
    if (isLimitReached(coupon)) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning">Limit Doldu</span>;
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success">Aktif</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Kupon Yönetimi</h2>
          <p className="text-sm text-muted-foreground mt-0.5">İndirim kodlarını oluşturun ve yönetin</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} iconName="Plus" iconPosition="left">
          Yeni Kupon
        </Button>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveView('list')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeView === 'list' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          <span className="flex items-center gap-2">
            <Icon name="Tag" size={14} />
            Kupon Listesi
          </span>
        </button>
        <button
          onClick={() => setActiveView('stats')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeView === 'stats' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          <span className="flex items-center gap-2">
            <Icon name="BarChart2" size={14} />
            Kupon İstatistikleri
          </span>
        </button>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-destructive text-sm">{error}</div>
      )}

      {/* Coupon List View */}
      {activeView === 'list' && (
        <div className="space-y-4">
          {/* Search */}
          <div className="relative max-w-sm">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Kupon ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2 border border-border rounded-lg bg-background text-foreground text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
            </div>
          ) : filteredCoupons?.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Icon name="Tag" size={40} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">Henüz kupon yok</p>
              <p className="text-sm mt-1">İlk kuponu oluşturmak için "Yeni Kupon" butonuna tıklayın</p>
            </div>
          ) : (
            <div className="bg-card rounded-xl border border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 font-semibold text-foreground">Kod</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground">İndirim</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground">Kullanım</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground">Son Tarih</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground">Bağlı Kullanıcı</th>
                      <th className="text-left px-4 py-3 font-semibold text-foreground">Durum</th>
                      <th className="text-right px-4 py-3 font-semibold text-foreground">İşlem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredCoupons?.map(coupon => (
                      <tr key={coupon?.id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3">
                          <div>
                            <span className="font-mono font-bold text-foreground tracking-wider">{coupon?.code}</span>
                            {coupon?.description && (
                              <p className="text-xs text-muted-foreground mt-0.5">{coupon?.description}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-foreground">
                          {coupon?.discount_type === 'percentage'
                            ? `%${coupon?.discount_value}`
                            : `₺${Number(coupon?.discount_value)?.toFixed(2)}`}
                          {coupon?.min_order_amount > 0 && (
                            <p className="text-xs text-muted-foreground">Min: ₺{coupon?.min_order_amount}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-foreground">
                          {coupon?.usage_count}
                          {coupon?.usage_limit !== null && (
                            <span className="text-muted-foreground"> / {coupon?.usage_limit}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-foreground">
                          {coupon?.expires_at
                            ? new Date(coupon?.expires_at)?.toLocaleDateString('tr-TR')
                            : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-3 text-foreground">
                          {coupon?.user_profiles?.full_name || coupon?.user_profiles?.email || (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(coupon)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleToggleActive(coupon)}
                              title={coupon?.is_active ? 'Pasif yap' : 'Aktif yap'}
                              className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                            >
                              <Icon
                                name={coupon?.is_active ? 'ToggleRight' : 'ToggleLeft'}
                                size={16}
                                className={coupon?.is_active ? 'text-success' : 'text-muted-foreground'}
                              />
                            </button>
                            <button
                              onClick={() => handleDelete(coupon?.id)}
                              title="Sil"
                              className="p-1.5 rounded-lg hover:bg-destructive/10 transition-colors"
                            >
                              <Icon name="Trash2" size={16} className="text-destructive" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stats View */}
      {activeView === 'stats' && (
        <div className="space-y-4">
          {statsLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
            </div>
          ) : stats?.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Icon name="BarChart2" size={40} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">Henüz kullanım verisi yok</p>
              <p className="text-sm mt-1">Kuponlar kullanıldıkça istatistikler burada görünecek</p>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-card rounded-xl border border-border p-4">
                  <p className="text-sm text-muted-foreground">Toplam Kullanım</p>
                  <p className="text-2xl font-bold text-foreground mt-1">
                    {stats?.reduce((s, r) => s + r?.usage_count, 0)}
                  </p>
                </div>
                <div className="bg-card rounded-xl border border-border p-4">
                  <p className="text-sm text-muted-foreground">Toplam İndirim</p>
                  <p className="text-2xl font-bold text-destructive mt-1">
                    ₺{stats?.reduce((s, r) => s + r?.total_discount, 0)?.toFixed(2)}
                  </p>
                </div>
                <div className="bg-card rounded-xl border border-border p-4">
                  <p className="text-sm text-muted-foreground">Kupon Geliri</p>
                  <p className="text-2xl font-bold text-success mt-1">
                    ₺{stats?.reduce((s, r) => s + r?.total_revenue, 0)?.toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Stats Table */}
              <div className="bg-card rounded-xl border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left px-4 py-3 font-semibold text-foreground">Kupon Kodu</th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground">İndirim</th>
                        <th className="text-right px-4 py-3 font-semibold text-foreground">Kullanım</th>
                        <th className="text-right px-4 py-3 font-semibold text-foreground">Toplam İndirim</th>
                        <th className="text-right px-4 py-3 font-semibold text-foreground">Oluşturulan Gelir</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {stats?.map(row => (
                        <tr key={row?.coupon_id} className="hover:bg-muted/20 transition-colors">
                          <td className="px-4 py-3">
                            <span className="font-mono font-bold text-foreground tracking-wider">{row?.code}</span>
                          </td>
                          <td className="px-4 py-3 text-foreground">
                            {row?.discount_type === 'percentage'
                              ? `%${row?.discount_value}`
                              : `₺${Number(row?.discount_value)?.toFixed(2)}`}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-foreground">{row?.usage_count}</td>
                          <td className="px-4 py-3 text-right text-destructive font-medium">
                            ₺{row?.total_discount?.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-right text-success font-medium">
                            ₺{row?.total_revenue?.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {showCreateModal && (
        <CreateCouponModal
          onClose={() => setShowCreateModal(false)}
          onCreated={loadCoupons}
          users={users}
        />
      )}
    </div>
  );
}
